import { ChevronLeftRounded } from '@mui/icons-material'
import { Avatar, Box, Button, Card, CardContent, IconButton, Typography, useMediaQuery } from '@mui/material'
import React from 'react'
import { AnimalDto } from '../../../interactors/gen/backendClient'
import { getReadableSterilizedForFerrets, getReadableSpecies } from '../../../domain/Animal/AnimalDisplay'
import { AnimalDtoSpecies } from '../../../interactors/gen/backendClient'
import { getAnimalProfilePhotoSrc } from '../../../utils/S3-links'
import { Link } from 'react-router-dom'
import { theme } from '../../theme'
import { addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { getSoftChipSx } from '../../common/chipStyles'

interface Props {
  animal: AnimalDto
  status: string
  backTo?: string
  onComplete?: () => void
  showCompleteAction?: boolean
  completeDisabled?: boolean
}

export const OnGoingAdoptionAnimalCard: React.FC<Props> = ({
  animal,
  status,
  backTo,
  onComplete,
  showCompleteAction = false,
  completeDisabled = false,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('md'))
  const statusTone =
    status.toLowerCase().includes('finaliser') || status.toLowerCase().includes('terminée') ? '#DCEFE3' : undefined
  const profileImageKey = animal?.images?.profileImageKey
  const animalProperties = [
    animal?.species && getReadableSpecies(animal?.species),
    /* animal?.birthday?.date && calculateAge(animal?.birthday?.date), */
    animal?.species === AnimalDtoSpecies.Ferret && animal?.health.sterilizedForFerrets
      ? getReadableSterilizedForFerrets(animal.health.sterilizedForFerrets)
      : animal?.health.sterilizedOrNeutered === 'true'
      ? 'Stérilisé'
      : 'Non stérilisé',
  ]
  return (
    <Card
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(122, 129, 151, 0.18)',
        boxShadow: '0 10px 28px rgba(26, 43, 77, 0.06)',
        my: { xs: 0, sm: 2 },
      }}
    >
      <CardContent sx={{ px: { xs: 1.5, sm: 2.25 }, py: { xs: 1.5, sm: 1.75 }, '&:last-child': { pb: 1.75 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: { xs: 0, sm: 8 },
            alignItems: 'center',
          }}
        >
          {/* Avatar, name + properties */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
            {backTo && (
              <IconButton
                component={Link}
                to={backTo}
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  border: '1px solid rgba(122, 129, 151, 0.2)',
                  backgroundColor: 'rgba(255,255,255,0.82)',
                  color: '#6E7890',
                  flexShrink: 0,
                  '&:hover': { backgroundColor: 'rgba(245, 247, 251, 0.96)' },
                  '& .MuiSvgIcon-root': { fontSize: { xs: 18, sm: 20 } },
                }}
              >
                <ChevronLeftRounded />
              </IconButton>
            )}
            {/* Avatar */}
            <Avatar
              alt={animal.name}
              src={profileImageKey && getAnimalProfilePhotoSrc(profileImageKey)}
              sx={{ width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 } }}
            />
            {/* Name, properties, (mobile: profile link) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  flexWrap: 'nowrap',
                }}
              >
                <Typography
                  variant="h5"
                  component={Link}
                  to={`/animaux/${animal.id}`}
                  sx={{
                    fontSize: { xs: '14px', sm: '19px' },
                    fontWeight: { xs: 700, sm: 'normal' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    color: '#1F3764',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {animal.name}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {animalProperties.join(' • ')}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            sx={{
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              alignItems: 'center',
              justifyContent: 'center',
              gap: {
                xs: 0.5,
                sm: 1,
                md: 1.25,
              },
              flexShrink: 0,
            }}
          >
            {!isMobile && (
              <Box
                sx={{
                  ...getSoftChipSx(statusTone, {
                    height: 32,
                    fontSize: 14,
                    labelWeight: 400,
                    maxWidth: 230,
                  }),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1.1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  {status}
                </Box>
              </Box>
            )}
            {showCompleteAction && !isTabletOrBelow && (
              <>
                <Typography sx={{ fontSize: 13, color: '#7A8197', fontWeight: 500, lineHeight: 1 }}>ou</Typography>
                <Button
                  variant="outlined"
                  sx={{
                    ...addSecondaryFabSx,
                    minHeight: isMobile ? 34 : 36,
                    height: isMobile ? 34 : 36,
                    fontSize: '14px',
                    px: isMobile ? 1.5 : 2,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={onComplete}
                  disabled={completeDisabled}
                >
                  Marquer comme terminée
                </Button>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
